import React, { useState, useEffect, useRef } from "react";
import _ from "lodash";
import TopNav from "../../commonUI/TopHeader/TopHeader";
import { sessionExpired } from "../../../config/messages";
import { serverUrl } from "../../../config/env";
import { messageRefreshInterval } from "../../../config/constants";
import {
  setMessageStatus,
  getConversation as getConversationApi,
  updateMessage as updateMessageApi,
  sendMessage as sendMessageApi,
} from "../../../services/messageService";
import {
  showSpinner,
  closeSpinner,
  modal,
  alert,
  adminModerateMessage,
  adminEditMessage,
} from "../../../js/modules/Dialogs";
import { toScreen, goBack } from "../../../js/modules/Routing";
import { BackArrowIcon, SendSubmitIcon } from "../../commonUI/Icons/SvgIcon";
import PropTypes from "prop-types";
import withNavigate from "../withNavigate";
import { io } from "socket.io-client";

const Message = ({ userDetails, userId, updateState, navigate }) => {
  // Instance-like values that never change — use refs so callbacks always see the same object
  const extraAttributes = useRef(window.history.state.usr);
  const joinRoomPayload = useRef(
    extraAttributes.current
      ? {
          receiverId: extraAttributes.current?.receiverId,
          receiverShortId: extraAttributes.current?.receiverShortId,
          receiverType: extraAttributes.current?.receiverType,
        }
      : null
  );
  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);
  const intervalRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const topSentinelRef = useRef(null);
  const isAppendingRef = useRef(false);
  const prevScrollHeightRef = useRef(0);

  // State
  const [updateOnUnmount, setUpdateOnUnmount] = useState({ messages: false });
  const [_id, setId] = useState("");
  const [short_id, setShortId] = useState("");
  const [conversation, setConversation] = useState([]);
  const [messages, setMessages] = useState({});
  const [newMessage, setNewMessage] = useState("");
  const [pagination, setPagination] = useState({ page: 1, total: 1 });
  const pageSize = 10;
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiSuggestions] = useState([
    "Thank you for your patience. We're working diligently to resolve this matter for you.",
    "We appreciate you bringing this to our attention and will address it immediately.",
  ]);
  const [participants, setParticipants] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Mirror state into refs so async callbacks / effects always read the latest value
  const short_idRef = useRef(short_id);
  const conversationRef = useRef(conversation);
  const participantsRef = useRef(participants);
  const updateOnUnmountRef = useRef(updateOnUnmount);
  const messagesRef = useRef(messages);
  const paginationRef = useRef(pagination);

  useEffect(() => { short_idRef.current = short_id; }, [short_id]);
  useEffect(() => { conversationRef.current = conversation; }, [conversation]);
  useEffect(() => { participantsRef.current = participants; }, [participants]);
  useEffect(() => { updateOnUnmountRef.current = updateOnUnmount; }, [updateOnUnmount]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  useEffect(() => { paginationRef.current = pagination; }, [pagination]);

  // ---------- helpers ----------

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // ---------- read-status ----------

  const setConversationReadStatus = () => {
    var theConversation = {
      accountInfo: {
        username: userDetails.accountInfo.username,
        email: userDetails.accountInfo.email,
      },
      conversation: short_idRef.current,
      message: "",
      readStatus: "read",
    };
    // setMessageStatus(theConversation, setConversationReadStatus_done, userDetails.jwttoken);
  };

  const setConversationReadStatus_done = (resStatus, responseString, resJson) => {
    if (resStatus == 401) {
      modal(sessionExpired);
      toScreen(false, navigate, "/logout");
    } else if (resStatus == "400") {
    } else if (resStatus == 200) {
      setUpdateOnUnmount({ messages: true });
      setMessages(resJson.messages);
    }
  };

  // ---------- fetch ----------

  const getConversation = async (event, explicitPage, append = false) => {
    if (event) {
      event.preventDefault();
    }

    if (!append && short_idRef.current === "") {
      showSpinner();
    }

    if (!extraAttributes.current?.receiverShortId || !extraAttributes.current?.receiverId) {
      if (!append) closeSpinner();
      modal("Invalid conversation data", "Error");
      return;
    }

    if (append) {
      // Save scroll height before new content is added at the visual top
      prevScrollHeightRef.current = scrollContainerRef.current?.scrollHeight || 0;
      isAppendingRef.current = true;
      setLoadingMore(true);
    }

    const currentPage = explicitPage !== undefined ? explicitPage : paginationRef.current.page;

    const queryParams = {
      receiverShortId: extraAttributes.current.receiverShortId,
      receiverId: extraAttributes.current.receiverId,
      requasterShortId:
        extraAttributes.current.userShortId1 === "admin"
          ? "Admin"
          : extraAttributes.current.userShortId1 || "Admin",
      page: currentPage,
      pageSize,
    };

    try {
      const resJson = await getConversationApi(queryParams);
      if (!append) closeSpinner();

      const newMessages =
        (resJson?.data?.messages && Object.values(resJson?.data?.messages)) || [];
      const totalPages = Math.ceil(resJson?.totalRecords / pageSize);

      setShortId(resJson?.data?.short_id);
      setParticipants(resJson?.data?.participants || []);
      setPagination((prev) => ({ ...prev, total: totalPages }));

      if (append) {
        // Append older messages to end of array — after .reverse() they appear at visual top
        setConversation((prev) => [...prev, ...newMessages]);
        setHasMore(currentPage < totalPages);
        setLoadingMore(false);
      } else {
        if (
          !_.isEqual(resJson?.data?.messages, conversationRef.current) ||
          !_.isEqual(resJson?.data?.participants, participantsRef.current)
        ) {
          setConversation(newMessages);
          setHasMore(1 < totalPages);
        }
        isAppendingRef.current = false;
        setConversationReadStatus();
      }
    } catch (error) {
      if (!append) closeSpinner();
      setLoadingMore(false);
      isAppendingRef.current = false;
      if (error.response?.status === 401) {
        modal(error.message || "401 Unauthorized", "Error", () =>
          window.history.back()
        );
      } else if (error.response?.status === 400) {
        console.error("Get conversation error:", error.response?.data);
        modal(
          error.response?.data?.message || error.message || "Something went wrong!",
          "Error",
          () => window.history.back()
        );
      } else {
        modal(error.message || "Error fetching conversation", "Error");
      }
    }
  };

  // ---------- update / moderate ----------

  // Calls the API with a pre-built conversation snapshot so we never depend on stale state
  const callUpdateMessageApi = (convSnapshot, key) => {
    const msg = convSnapshot[key];
    if (!msg) return;
    const theMessage = {
      conversation_short_id: short_idRef.current,
      message: {
        _id: msg._id,
        message: msg.message,
        moderation: {
          flags: msg.moderation?.flags || "",
          moderatorMessage: msg.moderation?.moderatorMessage || "",
          status: msg.moderation?.status || "",
        },
      },
    };
    updateMessageApi(theMessage)
      .then(() => {
        closeSpinner();
        getConversation(false);
      })
      .catch((error) => {
        closeSpinner();
        if (error.response?.status === 401) {
          modal(sessionExpired);
          toScreen(false, navigate, "/logout");
        } else {
          modal(error.message || "Error updating message");
        }
      });
  };

  const updateMessage = (event, key) => {
    if (event) {
      event.preventDefault();
    }
    if (conversationRef.current[key]) {
      callUpdateMessageApi(conversationRef.current, key);
    }
  };

  const moderateMessage = (event, key) => {
    if (event) {
      event.preventDefault();
    }

    if (conversationRef.current[key].userType !== "admin") {
      const approveMessageCallback = () => {
        showSpinner();
        const updatedConv = [...conversationRef.current];
        updatedConv[key] = {
          ...conversationRef.current[key],
          moderation: {
            ...conversationRef.current[key].moderation,
            status: "approved",
          },
        };
        setConversation(updatedConv);
        callUpdateMessageApi(updatedConv, key);
      };

      const saveMessageCallback = (theMode, theMessage, theModerateMessage) => {
        showSpinner();
        const baseConv = conversationRef.current;
        const updatedConv = [...baseConv];
        updatedConv[key] = {
          ...baseConv[key],
          message: theMode === "edit" ? theMessage : baseConv[key].message,
          moderation: {
            ...baseConv[key].moderation,
            status: theMode === "reject" ? "rejected" : "edited",
            moderatorMessage: theModerateMessage || "",
          },
        };
        setConversation(updatedConv);
        callUpdateMessageApi(updatedConv, key);
      };

      const editMessageCallback = () => {
        adminEditMessage(
          conversationRef.current[key],
          saveMessageCallback,
          "edit",
          participantsRef.current
        );
      };

      const rejectMessageCallback = () => {
        showSpinner();
        const updatedConv = [...conversationRef.current];
        const updatedMsg = {
          ...conversationRef.current[key],
          moderation: {
            ...conversationRef.current[key].moderation,
            status: "rejected",
          },
        };
        updatedConv[key] = updatedMsg;
        setConversation(updatedConv);
        // Pass the already-updated message directly (mirrors the setState-callback pattern)
        adminEditMessage(updatedMsg, saveMessageCallback, "reject", participantsRef.current);
      };

      adminModerateMessage(
        conversationRef.current[key],
        approveMessageCallback,
        rejectMessageCallback,
        editMessageCallback,
        participantsRef.current
      );
    }
  };

  // ---------- send / discard ----------

  const sendMessage = (event) => {
    if (newMessage) {
      showSpinner();

      const receiverId = participants
        ?.filter((item) => item?.user_short_id !== "Admin" && item?.user)
        ?.map((item) => item?.user)
        ?.join("");
      const requestBody = {
        conversationShortId: short_id || "",
        receiverId: receiverId || "",
        messageType: "conversation",
        message: newMessage,
      };
      sendMessageApi(requestBody)
        .then(() => {
          setNewMessage("");
          setPagination((prev) => ({ ...prev, page: 1 }));
          paginationRef.current = { ...paginationRef.current, page: 1 };
          closeSpinner();
          getConversation(false, 1);
        })
        .catch((error) => {
          closeSpinner();
          if (error.response?.status === 401) {
            modal("Please try again", "Something went wrong!");
          } else {
            modal(
              error.message || error.response?.data?.message || "Error sending message"
            );
          }
        });
    } else {
      modal("Please enter the message");
    }
  };

  const discardMessage = (event) => {
    if (event) {
      event.preventDefault();
    }
    setNewMessage("");
  };

  const handleInputChange = (event) => {
    const target = event.target;
    const value = target.type === "checkbox" ? target.checked : target.value;
    const name = target.name;

    if (name.split(".").length === 1) {
      if (name === "newMessage") setNewMessage(value);
    }
  };

  // ---------- lifecycle ----------

  // componentDidMount + componentWillUnmount
  useEffect(() => {
    getConversation();

    return () => {
      if (socketRef.current?.connected) {
        socketRef.current.emit("leave-room", {
          conversationId: short_idRef.current,
        });
        socketRef.current.disconnect();
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (updateOnUnmountRef.current.messages) {
        const updatedUserDetails = {
          ...userDetails,
          messages: messagesRef.current,
        };
        updateState("userDetails", updatedUserDetails);
      }
    };
  }, []);

  // Scroll to bottom on initial / send load; restore position after scroll-triggered append
  useEffect(() => {
    if (isAppendingRef.current) {
      if (scrollContainerRef.current && prevScrollHeightRef.current) {
        scrollContainerRef.current.scrollTop =
          scrollContainerRef.current.scrollHeight - prevScrollHeightRef.current;
        prevScrollHeightRef.current = 0;
      }
    } else {
      scrollToBottom();
    }
  }, [conversation]);

  // IntersectionObserver — load older messages when user scrolls to top sentinel
  useEffect(() => {
    const container = scrollContainerRef.current;
    const sentinel = topSentinelRef.current;
    if (!sentinel || !container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          const nextPage = paginationRef.current.page + 1;
          paginationRef.current = { ...paginationRef.current, page: nextPage };
          setPagination((prev) => ({ ...prev, page: nextPage }));
          getConversation(false, nextPage, true);
        }
      },
      { threshold: 0.1, root: container }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore]);

  // componentDidUpdate — socket new-message listener
  useEffect(() => {
    if (socketRef.current?.connected) {
      const handler = (recentMessage) => {
        if (recentMessage) {
          setConversation((prev) => [...prev, recentMessage]);
          setNewMessage("");
        }
      };
      socketRef.current.on("new-message", handler);
      return () => {
        socketRef.current?.off("new-message", handler);
      };
    }
  }, [conversation]);

  // ---------- render helpers ----------

  const renderMessages = (key) => {
    const message = conversation[key];
    const isAdminChat = message.userType === "admin";
    const isMavChat = message.userType === "mav";
    const isAdminViewing = extraAttributes.current.userShortId1 === "admin";

    const isRightAligned = isAdminChat || (!isAdminViewing && isMavChat);

    const matchingParticipant = participants.find(
      (participant) => participant.user === message.user
    );

    const printFlags = (theFlags) => {
      var flagString = " (";
      _.forEach(theFlags, function (value) {
        if (!flagString || flagString === " (") {
          flagString += '"' + value + '"';
        } else {
          flagString += ', "' + value + '"';
        }
      });
      flagString += " )";
      return flagString;
    };

    const username = isAdminChat
      ? "Admin Support"
      : matchingParticipant?.accountInfo?.username || "";

    const initials = isAdminChat
      ? "AS"
      : (username || "U")
          .split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
          .slice(0, 2);

    const canModerate = !isAdminChat;

    const bubbleClass = isAdminChat
      ? "admin-bubble"
      : isMavChat && isRightAligned
      ? "driver-bubble"
      : "user-bubble";

    const wrapperClass = isAdminChat
      ? "admin-message"
      : isRightAligned
      ? "mav-message"
      : "user-message";

    return (
      <div key={key} className={`chat-message ${wrapperClass}`}>
        <div className="message-content">
          {/* Avatar row */}
          <div className="message-avatar">
            {isAdminChat ? (
              <>
                <span className="admin-badge">AS</span>
                <span className="admin-label">Admin Support</span>
              </>
            ) : (
              <>
                <span
                  className={`msg-initials-avatar${isMavChat ? " mav-initials-avatar" : ""}`}
                  onClick={(e) => {
                    if (message.userType === "user" || message.userType === "mav") {
                      toScreen(
                        e,
                        navigate,
                        `/${message.userType === "user" ? "client" : "mav"}/${message?.user_short_id}`,
                        false,
                        extraAttributes.current
                      );
                    }
                  }}
                >
                  {initials}
                </span>
                <span className="sender-name">{username}</span>
              </>
            )}
          </div>

          {/* Message bubble */}
          <div
            className={`message-bubble ${bubbleClass}${
              message?.moderation?.status === "rejected" ? " bubble-rejected" : ""
            }`}
            onClick={canModerate ? (e) => moderateMessage(e, key) : undefined}
            style={canModerate ? { cursor: "pointer" } : {}}
          >
            {message.disputeFlag &&
              message.disputedJob_short_id &&
              message.disputedJob_short_id !== "" && (
                <button
                  onClick={(e) =>
                    toScreen(e, navigate, `/job/${message.disputedJob_short_id}`)
                  }
                >
                  <i className="u-font-size-12 u-color-red">
                    Disputing job:{" "}
                    <span className="u-uppercase">{message.disputedJob_short_id}</span>
                  </i>
                </button>
              )}
            {message.associatedQuoteShortID &&
              message.associatedQuoteShortID !== "" && (
                <i className="u-font-size-12">
                  &nbsp;Message about quote:{" "}
                  <span className="u-uppercase">{message.associatedQuoteShortID}</span>
                </i>
              )}
            <p
              className={`message-text${
                message?.moderation?.status === "flagged" ? " u-color-red" : ""
              }`}
            >
              {message.message}
            </p>
          </div>

          {/* Timestamp */}
          <span className="message-time">
            {message.time} on {message.date}
          </span>

          {/* Moderation status */}
          {message?.moderation?.status &&
            message.moderation.status !== "" &&
            message.moderation.status !== "approved" && (
              <div className="message-moderation-status">
                {message.moderation.status === "flagged" && (
                  <span className="u-color-red">
                    Flagged message
                    {message.moderation.flags &&
                    _.size(message.moderation.flags) > 0
                      ? printFlags(message.moderation.flags)
                      : null}
                  </span>
                )}
                {message.moderation.status === "rejected" && (
                  <span>Rejected message</span>
                )}
                {message.moderation.status === "edited" && (
                  <span>Edited message</span>
                )}
                {message.moderation.moderatorMessage &&
                  message.moderation.moderatorMessage !== "" && (
                    <span className="c-message-moderation-moderatorcomment">
                      "{message.moderation.moderatorMessage}"
                    </span>
                  )}
              </div>
            )}
        </div>
      </div>
    );
  };

  // ---------- render ----------

  const userParticipants = participants?.filter(
    (p) => p?.user_short_id !== "Admin" && p?.user_short_id !== "admin"
  );

  return (
    <>
      <TopNav title="Message" />
      <div className="global-body">
        <div className="global-body-container">
          <button
            type="button"
            className="btn-outline-light icon-with-btn mb-16"
            onClick={() =>
              toScreen(
                false,
                navigate,
                extraAttributes.current.previousPath,
                false,
                {
                  selectedFilter: extraAttributes.current.selectedFilter,
                  selectedPage: extraAttributes.current.selectedPage,
                }
              )
            }
          >
            <BackArrowIcon />
            Back
          </button>

          <div className="support-chat-container mt-16">
            {/* User Info Header */}
            <div className="chat-user-info">
              {userParticipants?.length > 0 ? (
                userParticipants.map((participant, idx) => {
                  const username = participant?.accountInfo?.username || "User";
                  const initials = (username || "U")
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .toUpperCase()
                    .slice(0, 2);
                  return (
                    <div
                      key={idx}
                      className="user-info"
                      onClick={(e) =>
                        toScreen(
                          e,
                          navigate,
                          `/${
                            participant?.userType === "mav" ? "mav" : "client"
                          }/${participant?.user_short_id}`
                        )
                      }
                    >
                      <span className="msg-initials-avatar">{initials}</span>
                      <div className="user-info-details">
                        <h3 className="user-info-name">{username}</h3>
                        <span className="user-info-id">
                          {participant?.user_short_id}
                        </span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="user-info-placeholder">
                  <span className="user-info-id">ID: {short_id}</span>
                </div>
              )}
            </div>

            {/* Messages Container */}
            <div className="messages-container" ref={scrollContainerRef}>
              <div className="messages-wrapper">
                {/* Top sentinel — IntersectionObserver triggers here to load older messages */}
                <div ref={topSentinelRef} style={{ height: 1 }} />
                {loadingMore && (
                  <div className="scroll-load-more">Loading older messages...</div>
                )}
                {conversation.length > 0
                  ? Object.keys(conversation).reverse().map(renderMessages)
                  : (conversation.length !== undefined || conversation) && (
                      <span className="error-text">No conversations found</span>
                    )}
                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* AI-Powered Suggestions */}
            <div className="ai-suggestions-section">
              <div className="ai-suggestions-header">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6L12 2z" />
                </svg>
                <span>AI-Powered Suggestions</span>
              </div>
              <div className="ai-prompt-wrapper">
                <input
                  type="text"
                  className="ai-prompt-input"
                  placeholder="Prompt to steer the suggestions"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                />
                <button className="ai-prompt-btn">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6L12 2z" />
                  </svg>
                </button>
              </div>
              <div className="ai-suggestion-chips">
                {aiSuggestions.map((suggestion, idx) => (
                  <button
                    key={idx}
                    className="ai-suggestion-chip"
                    onClick={() => setNewMessage(suggestion)}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>

            {/* Message Input */}
            <div className="message-input-container">
              <form
                className="message-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  sendMessage(e);
                }}
              >
                <div className="input-wrapper">
                  <textarea
                    rows="1"
                    className="message-input"
                    value={newMessage}
                    onChange={handleInputChange}
                    name="newMessage"
                    placeholder="Type a message..."
                    onKeyPress={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage(e);
                      }
                    }}
                  />
                  <button
                    type="submit"
                    className="send-button"
                    disabled={!newMessage?.trim()}
                  >
                  <SendSubmitIcon/>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

Message.propTypes = {
  userDetails: PropTypes.object.isRequired,
  userId: PropTypes.string.isRequired,
  updateState: PropTypes.func.isRequired,
};

export default withNavigate(Message);
